/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }

      if (this.documentClickHandler) {
        document.removeEventListener('click', this.documentClickHandler, true);
      }
      if (this.documentKeydownHandler) {
        document.removeEventListener('keydown', this.documentKeydownHandler, true);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;
      this._lastLg = Boolean(theme?.mediaMatches?.lg);

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();
      this.initDebug();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }

      this.syncHorizontalFilterGroups();
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterSearch.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
        this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
        window.addEventListener('resize', this.resizeHandler, { passive: true });

        // Close desktop dropdown filters when clicking anywhere outside, or pressing Escape.
        this.documentClickHandler = this.documentClickHandler || this.handleDocumentClick.bind(this);
        document.addEventListener('click', this.documentClickHandler, true);
        this.documentKeydownHandler = this.documentKeydownHandler || this.handleDocumentKeydown.bind(this);
        document.addEventListener('keydown', this.documentKeydownHandler, true);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Debug helper for diagnosing layout gaps (enabled via ?filtersdebug=1)
     */
    initDebug() {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('filtersdebug') !== '1') return;

        const stylesOf = (el) => {
          if (!el) return null;
          const cs = getComputedStyle(el);
          return {
            display: cs.display,
            position: cs.position,
            visibility: cs.visibility,
            height: cs.height,
            minHeight: cs.minHeight,
            maxHeight: cs.maxHeight,
            marginTop: cs.marginTop,
            marginBottom: cs.marginBottom,
            paddingTop: cs.paddingTop,
            paddingBottom: cs.paddingBottom,
            transform: cs.transform,
            overflow: cs.overflow
          };
        };

        const boxOf = (el) => {
          if (!el) return null;
          const r = el.getBoundingClientRect();
          return {
            top: Math.round(r.top),
            bottom: Math.round(r.bottom),
            height: Math.round(r.height)
          };
        };

        const overlay = document.createElement('div');
        overlay.id = 'filters-debug-overlay';
        overlay.style.cssText = [
          'position:fixed',
          'top:8px',
          'left:8px',
          'z-index:99999',
          'max-width:520px',
          'font:12px/1.35 ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace',
          'background:rgba(0,0,0,.78)',
          'color:#fff',
          'padding:10px 12px',
          'border-radius:10px',
          'white-space:pre-wrap'
        ].join(';');
        document.body.appendChild(overlay);

        const setOutline = (el, color) => {
          if (!el) return;
          el.style.outline = `2px dashed ${color}`;
          el.style.outlineOffset = '2px';
        };

        const root = this.closest('.main-products-grid') || document.querySelector('.main-products-grid');
        const filtersWrap = document.querySelector('.main-products-grid__filters--horizontal') || document.querySelector('.main-products-grid__filters');
        const resultsWrap = document.getElementById('filter-results')?.closest('.main-products-grid__results') || document.querySelector('.main-products-grid__results');
        const toolbar = document.getElementById('products-toolbar') || document.querySelector('products-toolbar');

        setOutline(toolbar, '#ffcc00');
        setOutline(filtersWrap, '#00e5ff');
        setOutline(this, '#ff66cc');
        setOutline(resultsWrap, '#66ff66');

        const update = () => {
          const payload = {
            viewport: { w: window.innerWidth, h: window.innerHeight, scrollY: Math.round(window.scrollY) },
            toolbar: { box: boxOf(toolbar), css: stylesOf(toolbar) },
            filtersWrap: { box: boxOf(filtersWrap), css: stylesOf(filtersWrap) },
            facetFilters: { box: boxOf(this), css: stylesOf(this) },
            facetsContent: { box: boxOf(this.querySelector('.facets__content') || this.querySelector('.drawer__content')), css: stylesOf(this.querySelector('.facets__content') || this.querySelector('.drawer__content')) },
            resultsWrap: { box: boxOf(resultsWrap), css: stylesOf(resultsWrap) }
          };
          overlay.textContent = JSON.stringify(payload, null, 2);
        };

        update();
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        document.addEventListener('on:facet-filters:updated', () => setTimeout(update, 50));
      } catch (e) {
        // ignore
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
        this.fixHorizontalFiltersWrap();
        this.syncHorizontalFilterGroups();
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
        this.syncHorizontalFilterGroups();
      }
    }

    handleResize() {
      if (!this.isHorizontalDesktopMode?.()) return;

      const isLg = Boolean(theme?.mediaMatches?.lg);
      if (this._lastLg !== isLg) {
        this._lastLg = isLg;
        this.handleBreakpointChange();
        return;
      }

      if (!isLg) return;

      // On desktop, keep groups stable and recenter when needed.
      if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = requestAnimationFrame(() => {
        try {
          // Ensure groups are in the right containers (resize may not fire on:breakpoint-change).
          this.syncHorizontalFilterGroups();

          const primary = this.querySelector('.js-facets-primary');
          if (!primary) return;
          const fits = primary.scrollWidth <= (primary.clientWidth + 2);
          if (fits) primary.setAttribute('data-centered', 'true');
          else primary.removeAttribute('data-centered');
        } catch (e) {
          // ignore
        }
      });
    }

    syncHorizontalFilterGroups() {
      try {
        if (!this.isHorizontalDesktopMode?.()) return;

        const primary = this.querySelector('.js-facets-primary');
        const moreBtn = this.querySelector('.js-more-filters');
        const secondary = this.querySelector('.js-facets-secondary');
        const source = this.querySelector('.js-facets-source');
        if (!primary || !moreBtn || !secondary || !source) return;

        const getFilterWrappersFrom = (root) => {
          const wraps = Array.from(root?.querySelectorAll?.('details-disclosure') || []);
          return wraps.filter((wrap) => {
            const details = wrap.querySelector('details.filter.disclosure');
            if (!details?.id) return false;
            if (!details.id.startsWith('filter-')) return false;
            if (details.id === 'filter-sort') return false;
            return true;
          });
        };

        const restoreToSource = () => {
          const allMoved = Array.from(new Set([
            ...getFilterWrappersFrom(primary),
            ...getFilterWrappersFrom(secondary)
          ]));

          if (!allMoved.length) return;

          moreBtn.hidden = false;
          moreBtn.setAttribute('aria-expanded', 'false');
          moreBtn.setAttribute('aria-hidden', 'true');
          secondary.hidden = true;
          secondary.setAttribute('aria-hidden', 'true');
          this.removeAttribute('data-more-filters');

          const ordered = allMoved
            .filter((el) => el.dataset)
            .sort((a, b) => (Number(a.dataset.filterOrder || 0) - Number(b.dataset.filterOrder || 0)));

          ordered.forEach((el) => source.appendChild(el));
          primary.setAttribute('aria-hidden', 'true');
          primary.removeAttribute('data-centered');
          this.removeAttribute('data-filter-groups');
        };

        if (!theme.mediaMatches.lg) {
          restoreToSource();
          return;
        }

        // Desktop: build an ordered list of filters (prefer source order).
        let filters = getFilterWrappersFrom(source);
        if (!filters.length) {
          filters = Array.from(new Set([
            ...getFilterWrappersFrom(primary),
            ...getFilterWrappersFrom(secondary)
          ]));
        }
        if (!filters.length) return;

        filters.forEach((el, idx) => {
          if (!el.dataset.filterOrder) el.dataset.filterOrder = String(idx);
        });
        filters.sort((a, b) => (Number(a.dataset.filterOrder) - Number(b.dataset.filterOrder)));

        // Move into: 5 primary + overflow in the second row.
        const PRIMARY_LIMIT = 5;
        let primaryCount = 0;
        const overflow = [];

        filters.forEach((el) => {
          if (primaryCount < PRIMARY_LIMIT) {
            primary.insertBefore(el, moreBtn);
            primaryCount += 1;
          } else {
            overflow.push(el);
          }
        });

        overflow.forEach((el) => secondary.appendChild(el));

        const hasOverflow = overflow.length > 0;
        moreBtn.hidden = !hasOverflow;
        moreBtn.setAttribute('aria-hidden', hasOverflow ? 'false' : 'true');
        if (!hasOverflow) {
          moreBtn.setAttribute('aria-expanded', 'false');
          secondary.hidden = true;
          secondary.setAttribute('aria-hidden', 'true');
          this.removeAttribute('data-more-filters');
        }

        primary.setAttribute('aria-hidden', 'false');
        this.setAttribute('data-filter-groups', 'ready');

        // Center the 5 primary CTAs when they fit without horizontal overflow.
        requestAnimationFrame(() => {
          try {
            const fits = primary.scrollWidth <= (primary.clientWidth + 2);
            if (fits) primary.setAttribute('data-centered', 'true');
            else primary.removeAttribute('data-centered');
          } catch (e) {
            // ignore
          }
        });
      } catch (e) {
        // ignore
      }
    }

    /**
     * Some environments (notably theme editor fullscreen) can temporarily apply drawer sizing
     * to the filters wrapper, creating a large blank gap. We defensively reset any inline sizing.
     */
    fixHorizontalFiltersWrap() {
      if (!this.isHorizontalDesktopMode?.() || !theme.mediaMatches.lg) return;

      const wrap = this.closest('.main-products-grid__filters--horizontal');
      const bar = this.closest('.main-products-grid__filters-bar');
      if (!wrap && !bar) return;

      const clearInlineSizing = () => {
        [wrap, bar].filter(Boolean).forEach((el) => {
          el.style.removeProperty('height');
          el.style.removeProperty('min-height');
          el.style.removeProperty('max-height');
        });
      };

      // Run now and again on next frames to override late-applied inline styles.
      clearInlineSizing();
      requestAnimationFrame(clearInlineSizing);
      requestAnimationFrame(() => requestAnimationFrame(clearInlineSizing));
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Ignore filter option search inputs (local UI only).
      if (evt.target?.matches?.('[data-filter-search]')) return;

      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Filters values within a single filter list (client-side).
     * @param {object} evt - Event object.
     */
    handleFilterSearch(evt) {
      const { target } = evt;
      if (!target?.matches?.('[data-filter-search]')) return;

      const query = (target.value || '').trim().toLowerCase();
      const filter = target.closest('.filter');
      if (!filter) return;

      const listItems = filter.querySelectorAll('ul[role="list"] > li');
      listItems.forEach((li) => {
        const text = (li.textContent || '').trim().toLowerCase();
        // Preserve existing "show more" behavior via .js-hidden (CSS), but allow additional hiding via [hidden].
        li.hidden = query ? !text.includes(query) : false;
      });
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Desktop: toggle "Plus de filtres" second row.
      const moreBtn = target.closest?.('.js-more-filters');
      if (moreBtn && theme.mediaMatches.lg && this.isHorizontalDesktopMode()) {
        evt.preventDefault();
        this.toggleMoreFilters();
        return;
      }

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }

      // Desktop: when a filter is toggled, close other open dropdowns.
      const summary = target.closest?.('summary.filter__toggle');
      if (summary && theme.mediaMatches.lg && this.isHorizontalDesktopMode()) {
        const currentFilter = summary.closest('details.filter');
        if (currentFilter) {
          this.closeAllDesktopFiltersExcept(currentFilter);
        }
      }
    }

    toggleMoreFilters(forceOpen = null) {
      if (!this.isHorizontalDesktopMode?.() || !theme.mediaMatches.lg) return;
      const moreBtn = this.querySelector('.js-more-filters');
      const secondary = this.querySelector('.js-facets-secondary');
      if (!moreBtn || !secondary || moreBtn.hidden) return;

      const isOpen = forceOpen !== null ? Boolean(forceOpen) : !(this.getAttribute('data-more-filters') === 'open');
      if (isOpen) {
        this.setAttribute('data-more-filters', 'open');
        moreBtn.setAttribute('aria-expanded', 'true');
        secondary.hidden = false;
        secondary.setAttribute('aria-hidden', 'false');
      } else {
        this.removeAttribute('data-more-filters');
        moreBtn.setAttribute('aria-expanded', 'false');
        secondary.hidden = true;
        secondary.setAttribute('aria-hidden', 'true');
        this.closeAllDesktopFiltersExcept(null);
      }
    }

    isHorizontalDesktopMode() {
      return Boolean(this.closest('.main-products-grid__filters--horizontal'));
    }

    closeAllDesktopFiltersExcept(exceptEl = null) {
      const openFilters = this.querySelectorAll(
        '.facets__filters--primary > details-disclosure > details.filter[open], .facets__filters--secondary details.filter[open]'
      );
      openFilters.forEach((el) => {
        if (exceptEl && el === exceptEl) return;
        el.open = false;
      });
    }

    handleDocumentClick(evt) {
      if (!theme.mediaMatches.lg) return;
      if (!this.isHorizontalDesktopMode()) return;

      // If click is inside an open filter (summary or dropdown panel), keep it open.
      const openDetails = evt.target?.closest?.('details.filter[open]');
      if (openDetails && this.contains(openDetails)) return;

      // Otherwise close all open dropdowns.
      this.closeAllDesktopFiltersExcept(null);
    }

    handleDocumentKeydown(evt) {
      if (!theme.mediaMatches.lg) return;
      if (!this.isHorizontalDesktopMode()) return;
      if (evt.key !== 'Escape') return;

      this.closeAllDesktopFiltersExcept(null);
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
